import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { AppLink } from '../components/Nav';
import { LoadMore, Rows, Table, Td, Th, Tr } from '../components/data';
import { Badge, Banner, Button, CopyButton, Input, PageHeader, cx } from '../components/ui';
import { track } from '../lib/amplitude';
import { errorMessage } from '../lib/api';
import { shortDate, timeAgo } from '../lib/format';
import {
  createCompInvite,
  fetchCompInvites,
  mintCompInviteLink,
  qk,
  resendCompInvite,
  withdrawCompInvite,
  type CompInviteRow,
} from '../queries/admin';

/**
 * Setting somebody up before they have ever signed up: a team of their own,
 * comped, with them as owner. Every subscriber has a team, paid or given, so
 * a team of one is the smallest gift and more seats the bigger one. The
 * account and the team exist the moment the form is sent; the email carries
 * a link that signs them in and lands them in the welcome steps with the
 * plan already on.
 *
 * The table says what happened next, because "invited" and "received the
 * email" and "actually came in" are three different things.
 */

/** `<input type="date">` speaks YYYY-MM-DD; the API wants an instant. */
function isoFromDateInput(value: string): string | null {
  if (!value) return null;
  const date = new Date(`${value}T23:59:59`);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function tomorrow(): string {
  const date = new Date(Date.now() + 86_400_000);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

function InviteForm() {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [teamName, setTeamName] = useState('');
  const [seats, setSeats] = useState('1');
  const [until, setUntil] = useState('');
  const [reason, setReason] = useState('');

  const send = useMutation({
    mutationFn: () =>
      createCompInvite({
        email: email.trim(),
        name: name.trim() || null,
        until: isoFromDateInput(until),
        reason: reason.trim() || null,
        team: { name: teamName.trim() || null, seats: Number(seats) || 1 },
      }),
    onSuccess: () => {
      track('Comp Invite Sent', { seats: Number(seats) || 1 });
      setEmail('');
      setName('');
      setTeamName('');
      setReason('');
      return queryClient.invalidateQueries({ queryKey: [...qk.compInvites()] });
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    send.mutate();
  };

  const sent = send.data;
  return (
    <form onSubmit={submit} className="card overflow-hidden">
      <div className="px-4 pt-4">
        <h2 className="text-h4 text-chalk">Invite someone new, on us</h2>
        <p className="mt-1 text-body text-chalk-dim">
          Their account and a team of their own are made now, comped, with them as owner. One seat is just them; more
          seats and they invite the rest from Settings. Somebody who already has an account gets a team from their own
          page instead.
        </p>
      </div>

      <div className="mt-4 border-t border-hairline px-4 py-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr] sm:items-end">
          <Input label="Email" type="email" autoComplete="off" required value={email} onChange={(event) => setEmail(event.target.value)} />
          <Input label="Their name (optional)" autoComplete="off" maxLength={80} value={name} onChange={(event) => setName(event.target.value)} />
        </div>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_7rem] sm:items-end">
          <Input
            label="Team name (optional)"
            autoComplete="off"
            maxLength={80}
            placeholder={name.trim() ? `${name.trim().split(/\s+/)[0]}'s team` : "Their name, or the company"}
            value={teamName}
            onChange={(event) => setTeamName(event.target.value)}
          />
          <Input
            label="Seats"
            type="number"
            min={1}
            max={1000}
            step={1}
            inputMode="numeric"
            required
            value={seats}
            onChange={(event) => setSeats(event.target.value)}
          />
        </div>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[11rem_1fr] sm:items-end">
          <Input label="Expires" type="date" min={tomorrow()} value={until} onChange={(event) => setUntil(event.target.value)} />
          <Input
            label="Reason"
            placeholder="Pilot agreed by email, friend of the house, conference giveaway…"
            maxLength={200}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
        <p className="mt-2 text-caption text-chalk-faint">
          A blank expiry never lapses. A blank team name becomes theirs by name, and they can rename it once they are
          in. The email signs them in as this address, once, and the link lasts 14 days; the grant does not depend on it.
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-hairline bg-slate-900/40 px-4 py-3">
        <Button type="submit" variant="primary" busy={send.isPending}>
          Make the team and send the invite
        </Button>
      </div>

      {send.isError ? (
        <div className="px-4 pb-4">
          <Banner tone="danger">{errorMessage(send.error)}</Banner>
        </div>
      ) : null}
      {sent ? (
        <div className="px-4 pb-4">
          {sent.sent.error ? (
            <Banner tone="caution" title={`${sent.email} is set up, but the email did not go out.`}>
              {sent.sent.error} The account{sent.org ? ' and the team' : ''} exist; use Resend in the table to try the
              email again.
            </Banner>
          ) : (
            <Banner tone="go" title={`${sent.email} is set up${sent.org ? ` as the owner of ${sent.org.name}` : ''}.`}>
              An email with the sign-in link is on its way to them.
            </Banner>
          )}
        </div>
      ) : null}
    </form>
  );
}

/** What the invitation did, in one line, and whether it needs another go. */
function InviteStatus({ invite }: { invite: CompInviteRow }) {
  const { sent } = invite;
  if (invite.signedIn) {
    return (
      <span className="flex flex-col gap-0.5">
        <Badge tone="go">Signed in</Badge>
        <span className="text-caption text-chalk-faint">
          {invite.user.onboardedAt ? 'finished the welcome steps' : invite.acceptedAt ? `came in ${timeAgo(invite.acceptedAt)}` : ''}
        </span>
      </span>
    );
  }
  if (sent.error) {
    return (
      <span className="flex flex-col gap-0.5">
        <Badge tone="danger">Email failed</Badge>
        <span className="max-w-[18rem] text-caption text-chalk-dim" title={sent.error}>
          {sent.error}
        </span>
      </span>
    );
  }
  const expired = new Date(invite.expiresAt).getTime() < Date.now();
  if (sent.at) {
    return (
      <span className="flex flex-col gap-0.5">
        <Badge tone={expired ? 'caution' : 'info'}>{expired ? 'Link expired' : 'Invited'}</Badge>
        <span className="text-caption text-chalk-faint">
          email sent {timeAgo(sent.at)}
          {sent.count > 1 ? `, ${sent.count} in all` : ''}
        </span>
      </span>
    );
  }
  return (
    <span className="flex flex-col gap-0.5">
      <Badge tone="neutral">Not sent</Badge>
      <span className="text-caption text-chalk-faint">no email has gone out</span>
    </span>
  );
}

/** The grant, as it stands now on the account or the team. */
function Grant({ invite }: { invite: CompInviteRow }) {
  const comp = invite.org ? invite.org.comp : invite.user.comp;
  if (!comp) return <span className="text-chalk-faint">none</span>;
  return (
    <span className="flex flex-col gap-0.5">
      <span className="flex flex-wrap items-center gap-1.5">
        <Badge tone={comp.active ? 'go' : 'neutral'}>{comp.active ? 'Comped' : 'Lapsed'}</Badge>
        {invite.org ? <span className="text-caption text-chalk-dim">{invite.org.seats.comped} seats</span> : null}
      </span>
      <span className="text-caption text-chalk-faint">
        {comp.until ? `${comp.active ? 'until' : 'ended'} ${shortDate(comp.until)}` : 'no expiry'}
        {comp.reason ? ` · ${comp.reason}` : ''}
      </span>
    </span>
  );
}

/**
 * The two links, told apart. The page link is on the row and copies with a
 * click: paste it anywhere, it only offers to send the email. The sign-in
 * link is minted when asked for, copied straight to the clipboard and shown
 * once, because it signs the reader in as the invited address and replaces
 * the one in their inbox.
 */
function InviteLinks({ invite }: { invite: CompInviteRow }) {
  const [minted, setMinted] = useState<{ url: string; signsIn: boolean } | null>(null);
  const [copied, setCopied] = useState(false);
  const mint = useMutation({
    mutationFn: () => mintCompInviteLink(invite.id),
    onSuccess: async (link) => {
      track('Comp Invite Link Copied', { signsIn: link.signsIn });
      setMinted(link);
      try {
        await navigator.clipboard.writeText(link.url);
        setCopied(true);
      } catch {
        setCopied(false);
      }
    },
  });
  if (invite.signedIn) {
    return <span className="text-caption text-chalk-faint">They are in; the link has done its job.</span>;
  }
  return (
    <span className="flex flex-col gap-1.5">
      <span className="flex items-center gap-2">
        <a href={invite.startUrl} target="_blank" rel="noopener noreferrer" className="max-w-[16rem] truncate text-caption text-chalk-dim hover:text-chalk" title={invite.startUrl}>
          {invite.startUrl.replace(/^https?:\/\//, '')}
        </a>
        <CopyButton value={invite.startUrl} label="Copy" copiedLabel="Copied" size="sm" variant="ghost" />
      </span>
      <span className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="ghost" busy={mint.isPending} onClick={() => mint.mutate()}>
          {minted ? 'Copy a fresh sign-in link' : 'Copy sign-in link'}
        </Button>
        {minted ? (
          <span className={cx('text-caption', copied ? 'text-go' : 'text-chalk-dim')}>
            {copied ? 'Copied. ' : ''}
            {minted.signsIn
              ? 'Signs them in once; the link in their email no longer does. Send it to them and nobody else.'
              : 'This deployment cannot mint a sign-in code; the link only opens the page.'}
          </span>
        ) : null}
        {mint.isError ? <span className="text-caption text-danger">{errorMessage(mint.error)}</span> : null}
      </span>
      {minted && !copied ? (
        <input readOnly value={minted.url} onFocus={(event) => event.currentTarget.select()} className="w-full rounded border border-hairline bg-slate-950 px-2 py-1 text-caption text-chalk" />
      ) : null}
    </span>
  );
}

function RowActions({ invite }: { invite: CompInviteRow }) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const refresh = () => queryClient.invalidateQueries({ queryKey: [...qk.compInvites()] });

  const resend = useMutation({
    mutationFn: () => resendCompInvite(invite.id),
    onSuccess: () => {
      track('Comp Invite Resent');
      return refresh();
    },
  });
  const withdraw = useMutation({
    mutationFn: () => withdrawCompInvite(invite.id),
    onSuccess: ({ removed }) => {
      track('Comp Invite Withdrawn', { account: removed.account, team: removed.team });
      return refresh();
    },
  });

  if (confirming) {
    const clean = !invite.signedIn;
    return (
      <span className="flex flex-col items-end gap-1.5">
        <span className="max-w-[16rem] text-right text-caption text-chalk-dim">
          {clean
            ? `Nobody has come in. The account${invite.org ? ' and the team' : ''} go too.`
            : 'They have signed in, so the account stays. Only the link goes; revoke the grant from their page.'}
        </span>
        <span className="flex items-center gap-1.5">
          <Button size="sm" variant="danger" busy={withdraw.isPending} onClick={() => withdraw.mutate()}>
            Withdraw
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setConfirming(false)} disabled={withdraw.isPending}>
            Keep
          </Button>
        </span>
        {withdraw.isError ? <span className="text-caption text-danger">{errorMessage(withdraw.error)}</span> : null}
      </span>
    );
  }

  return (
    <span className="flex flex-col items-end gap-0.5">
      <span className="flex items-center gap-1.5">
        {!invite.signedIn ? (
          <Button size="sm" variant={invite.sent.error ? 'secondary' : 'ghost'} busy={resend.isPending} onClick={() => resend.mutate()}>
            {invite.sent.count > 0 ? 'Resend' : 'Send'}
          </Button>
        ) : null}
        <Button size="sm" variant="ghost" onClick={() => setConfirming(true)}>
          Withdraw
        </Button>
      </span>
      {resend.isError ? <span className="text-caption text-danger">{errorMessage(resend.error)}</span> : null}
      {resend.isSuccess ? (
        <span className={cx('text-caption', resend.data.sent.error ? 'text-danger' : 'text-go')}>
          {resend.data.sent.error ? 'Still failing' : 'Sent'}
        </span>
      ) : null}
    </span>
  );
}

export function InvitesPage() {
  useEffect(() => {
    track('Viewed Admin Invites');
  }, []);

  const invites = useInfiniteQuery({
    queryKey: qk.compInvites(),
    queryFn: ({ pageParam }) => fetchCompInvites(pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
  const rows = invites.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <>
      <PageHeader title="Invites" subtitle="People we set up ourselves, each with a comped team, before they had ever signed up." />
      <div className="mb-6">
        <InviteForm />
      </div>

      <Table>
        <thead>
          <tr>
            <Th>Invited</Th>
            <Th>Given</Th>
            <Th>Grant</Th>
            <Th>Status</Th>
            <Th>Link</Th>
            <Th>Sent by</Th>
            <Th align="right" />
          </tr>
        </thead>
        <Rows query={invites} columns={7} empty="Nobody has been invited this way yet.">
          {rows.map((invite) => (
            <Tr key={invite.id}>
              <Td className="max-w-[18rem]">
                <AppLink to="/users/$id" params={{ id: invite.user.id }} className="block min-w-0 hover:text-chalk">
                  <span className="block truncate text-chalk">{invite.name?.trim() || invite.email}</span>
                  {invite.name ? <span className="block truncate text-caption text-chalk-faint">{invite.email}</span> : null}
                </AppLink>
              </Td>
              <Td>
                {invite.org ? (
                  <AppLink to="/teams/$id" params={{ id: invite.org.id }} className="hover:text-chalk">
                    <span className="text-chalk">{invite.org.name}</span>
                    <span className="block text-caption text-chalk-faint">a team of their own, as owner</span>
                  </AppLink>
                ) : (
                  <span className="text-chalk-dim">The paid plan, no team (an older invitation)</span>
                )}
              </Td>
              <Td>
                <Grant invite={invite} />
              </Td>
              <Td>
                <InviteStatus invite={invite} />
              </Td>
              <Td>
                <InviteLinks invite={invite} />
              </Td>
              <Td className="text-chalk-dim">
                <span className="block truncate">{invite.invitedBy}</span>
                <span className="block text-caption text-chalk-faint" title={invite.createdAt}>
                  {shortDate(invite.createdAt)}
                </span>
              </Td>
              <Td align="right">
                <RowActions invite={invite} />
              </Td>
            </Tr>
          ))}
        </Rows>
      </Table>
      <LoadMore query={invites} />
    </>
  );
}
